import { v4 as uuidv4 } from "uuid";

import { StotlesBlock, StotlesDOCXExporter } from "components/ui/blocknote_editor/types";
import { sanitizeBlockColors, validateInitialContent } from "components/ui/blocknote_editor/utils";

export interface BidResponseForExport {
  title?: string | null;
  section?: string | null;
  reference?: string | null;
  description?: string | null;
  response?: string | null;
  createdAt?: string | null;
}

export function sortResponsesForExport(responses: BidResponseForExport[]): BidResponseForExport[] {
  return [...responses].sort((a, b) => {
    // Primary: section (alphabetical, nulls last)
    if (a.section !== b.section) {
      if (!a.section) return 1;
      if (!b.section) return -1;
      return a.section.localeCompare(b.section);
    }

    // Secondary: reference (numeric-aware so "A2" < "A10")
    if (a.reference !== b.reference) {
      if (!a.reference) return 1;
      if (!b.reference) return -1;
      return a.reference.localeCompare(b.reference, undefined, { numeric: true });
    }

    // Tertiary: createdAt ascending — ISO 8601 strings sort correctly with plain comparison
    const dateA = a.createdAt ?? "";
    const dateB = b.createdAt ?? "";
    return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
  });
}

function buildHeadingBlock(text: string, level: 1 | 2): StotlesBlock {
  return {
    id: uuidv4(),
    type: "heading",
    props: {
      level,
      isToggleable: false,
      backgroundColor: "default" as const,
      textColor: "default" as const,
      textAlignment: "left" as const,
    },
    content: [{ type: "text", text, styles: {} }],
    children: [],
  };
}

function buildParagraphBlock(text: string, styles: Record<string, boolean> = {}): StotlesBlock {
  return {
    id: uuidv4(),
    type: "paragraph",
    props: {
      backgroundColor: "default" as const,
      textColor: "default" as const,
      textAlignment: "left" as const,
    },
    content: text ? [{ type: "text", text, styles }] : [],
    children: [],
  };
}

function buildSeparatorBlock(): StotlesBlock {
  return {
    id: uuidv4(),
    type: "paragraph",
    props: {
      backgroundColor: "default" as const,
      textColor: "default" as const,
      textAlignment: "center" as const,
    },
    content: [
      {
        type: "text",
        text: "___________________________________________________________________",
        styles: {},
      },
    ],
    children: [],
  };
}

export async function getCombinedDocxBlob(
  responses: BidResponseForExport[],
  documentTitle?: string,
): Promise<Blob> {
  const exporter = StotlesDOCXExporter;

  const combinedBlocks: StotlesBlock[] = [];

  if (documentTitle) {
    combinedBlocks.push(buildHeadingBlock(documentTitle, 1));
    combinedBlocks.push(buildSeparatorBlock());
    combinedBlocks.push(buildParagraphBlock(""));
  }

  const sorted = sortResponsesForExport(responses);

  for (const response of sorted) {
    combinedBlocks.push(buildHeadingBlock(response.title || "Untitled response", 2));

    if (response.description) {
      combinedBlocks.push(buildParagraphBlock(response.description, { italic: true }));
    }

    combinedBlocks.push(buildSeparatorBlock());

    const content = validateInitialContent(response.response);
    if (content && content.length > 0) {
      combinedBlocks.push(...(sanitizeBlockColors(content) as StotlesBlock[]));
    } else {
      combinedBlocks.push(buildParagraphBlock("(No response content)"));
    }

    combinedBlocks.push(buildParagraphBlock(""));
  }

  return exporter.toBlob(combinedBlocks);
}
